import React, { ReactNode, useContext } from "react";
import {
  DisclosureStateContext,
  Disclosure as ReactAriaDisclosure,
  DisclosurePanel as ReactAriaDisclosurePanel,
  DisclosurePanelProps as ReactAriaDisclosurePanelProps,
  DisclosureProps as ReactAriaDisclosureProps,
} from "react-aria-components";

import { cn } from "../utils";
import { NcButton, NcButtonProps } from "./nc-button";
import { NcIconChevronRight } from "./nc-icons";
import { NcLoadingIndicator } from "./nc-loading-indicator";

export interface DisclosureProps extends ReactAriaDisclosureProps {
  children: React.ReactNode;
}

export const NcDisclosure = ({ children, className, ...props }: DisclosureProps) => {
  return (
    <ReactAriaDisclosure
      data-nc="NcDisclosure"
      className={cn("group min-w-50", className)}
      {...props}
    >
      {children}
    </ReactAriaDisclosure>
  );
};

export type NcDisclosureTriggerProps = NcButtonProps;

const NcDisclosureTrigger = ({ children, className, ...props }: NcDisclosureTriggerProps) => {
  const { isExpanded } = useContext(DisclosureStateContext)!;
  return (
    <NcButton
      slot="trigger"
      variant="trigger"
      data-nc="NcDisclosure.Trigger"
      className={cn("w-full max-w-max justify-start gap-1 data-disabled:bg-transparent", className)}
      {...props}
    >
      <NcIconChevronRight
        aria-hidden="true"
        className={cn(
          "group-data-disabled:text-ui w-4 shrink-0 text-base transition-transform duration-200 ease-in-out",
          isExpanded && "rotate-90 transform"
        )}
      />
      <span>{children}</span>
    </NcButton>
  );
};

export interface NcDisclosurePanelBody extends ReactAriaDisclosurePanelProps {
  children: ReactNode;
  isLoading?: boolean;
}

function NcDisclosureBody({ children, isLoading, className, ...props }: NcDisclosurePanelBody) {
  return (
    <ReactAriaDisclosurePanel
      data-nc="NcDisclosure.Body"
      className={cn(
        "h-(--disclosure-panel-height) overflow-clip motion-safe:transition-[height]",
        className
      )}
      {...props}
    >
      <div className="py-2 pr-2 pl-5">{isLoading ? <NcLoadingIndicator /> : <>{children}</>}</div>
    </ReactAriaDisclosurePanel>
  );
}

NcDisclosure.Body = NcDisclosureBody;
NcDisclosure.Trigger = NcDisclosureTrigger;
